import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StaffHeader } from "@/components/StaffHeader";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BarChart3, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { v: "4w", l: "4 dernières semaines" },
  { v: "12w", l: "12 dernières semaines" },
  { v: "26w", l: "6 derniers mois" },
  { v: "52w", l: "12 derniers mois" },
  { v: "year", l: "Depuis le 1er janvier" },
];
const dFr = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";

const StatRow = ({ row, max, threshold }) => (
  <div className="py-3 border-b last:border-0" data-testid={`stat-row-${row.slug}`}>
    <div className="flex items-center justify-between gap-3 text-sm mb-1.5">
      <span className="font-medium">{row.name}</span>
      <span className="font-heading font-bold tabular-nums" data-testid={`stat-count-${row.slug}`}>
        {row.count == null ? <span className="text-xs font-normal text-muted-foreground">moins de {threshold}</span> : row.count}
      </span>
    </div>
    {row.count != null && <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full transition-[width] duration-500" style={{ width: `${Math.max(4, (row.count / max) * 100)}%` }} /></div>}
  </div>
);

export default function SpecialtyStats() {
  const [period, setPeriod] = useState("12w");
  const [pharmacyId, setPharmacyId] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = { period };
    if (pharmacyId !== "all") params.pharmacyId = pharmacyId;
    api.get("/stats/specialties", { params }).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [period, pharmacyId]);

  const max = Math.max(1, ...(data?.rows || []).map((r) => r.count || 0));

  return (
    <div className="min-h-screen bg-background">
      <StaffHeader />
      <main className="mx-auto max-w-4xl px-4 py-8" data-testid="specialty-stats-page">
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="h-7 w-7 text-primary" /> Recherches par spécialité</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-6">Volumes globaux de spécialités choisies sur Eymi. Aucune recherche individuelle n'est conservée.</p>

        <Card className="rounded-2xl p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Période</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="stats-period" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select></div>
          {data?.pharmacies?.length > 1 && (
            <div className="space-y-1.5"><Label className="text-xs">Pharmacie</Label>
              <Select value={pharmacyId} onValueChange={setPharmacyId}>
                <SelectTrigger data-testid="stats-pharmacy" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Toutes</SelectItem>
                  {data.pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
        </Card>

        {data && (
          <>
            <p className="text-xs text-muted-foreground mb-3" data-testid="stats-range">Du {dFr(data.from)} au {dFr(data.to)} · semaine en cours non incluse</p>
            <Card className="rounded-2xl p-5" data-testid="stats-list">
              {data.rows.length === 0
                ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="stats-empty">Aucune recherche sur cette période.</p>
                : data.rows.map((r) => <StatRow key={r.slug} row={r} max={max} threshold={data.threshold} />)}
              {data.rows.length > 0 && <p className="text-sm mt-4 pt-3 border-t" data-testid="stats-total">Total affiché : <b>{data.visibleTotal}</b> recherche(s){data.suppressedCategories > 0 && ` · ${data.suppressedCategories} spécialité(s) sous le seuil de ${data.threshold}`}</p>}
            </Card>
          </>
        )}

        <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 mt-6 flex gap-2" data-testid="stats-privacy">
          <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">Chaque choix de spécialité incrémente uniquement un compteur hebdomadaire global : ni date précise, ni adresse IP, ni identifiant patient. Les volumes inférieurs à {data?.threshold || 5} sont masqués et la semaine en cours n'est jamais affichée, afin d'éviter tout rapprochement avec une personne.</p>
        </div>
      </main>
    </div>
  );
}
